// API istemcisi. §7.0 hata gövdesini ({hata:{kod,mesaj,detay[]}}) tek yerde çözer,
// böylece ekranlar her çağrıda aynı hata biçimini görür.

export type HataDetay = { kod: string; mesaj: string; detay?: string[] }

export class ApiHatasi extends Error {
  constructor(
    readonly durum: number,
    readonly kod: string,
    mesaj: string,
    readonly detay?: string[],
  ) {
    super(mesaj)
    this.name = 'ApiHatasi'
  }

  /** K referanslı kural ihlali mi (409)? */
  get kuralIhlali() {
    return this.durum === 409
  }
}

let _jeton: string | null = null

export function jetonAyarla(jeton: string | null) {
  _jeton = jeton
}

export function jetonAl() {
  return _jeton
}

/**
 * Sunucu "bu oturum artık geçerli değil" dediğinde çağrılır.
 *
 * Neden gerekli: jetonun ömrü dolduğunda ya da oturum başka bir cihazdan
 * kapatıldığında (ekran 13 "diğer oturumları kapat", ekran 14 kullanıcıyı
 * pasife alma) her istek 401 dönüyor, ama istemci hiçbir şey yapmıyordu:
 * kabuk açık kalıyor, tablolar "Kayıt yok" gösteriyor, KPI'lar "—" basıyordu.
 * Kullanıcı ekranda VERİ OLMADIĞINI görüyor, oturumunun düştüğünü değil —
 * ekranlar boş veri ile gerçekten boş oturumu ayırt edemiyordu.
 *
 * Kancayı OturumSaglayici kuruyor; oturumu düşürünce Korumali zaten /giris'e
 * yönlendiriyor.
 */
let _oturumDustu: (() => void) | null = null

export function oturumDustuKancasi(kanca: (() => void) | null) {
  _oturumDustu = kanca
}

type Secenek = {
  yontem?: 'GET' | 'POST' | 'PUT' | 'DELETE'
  govde?: unknown
  /** Tesis seçimi adımında geçiş jetonu kullanılır, oturum jetonu değil. */
  jeton?: string
}

export async function api<T>(yol: string, secenek: Secenek = {}): Promise<T> {
  const { yontem = 'GET', govde, jeton = _jeton } = secenek

  const basliklar: Record<string, string> = {}
  if (govde !== undefined) basliklar['Content-Type'] = 'application/json'
  if (jeton) basliklar['Authorization'] = `Bearer ${jeton}`

  const yanit = await fetch(yol, {
    method: yontem,
    headers: basliklar,
    body: govde === undefined ? undefined : JSON.stringify(govde),
  })

  if (!yanit.ok) {
    // Hata gövdesi okunamazsa (ör. vekil 502 döndürdü) durum koduyla yetin —
    // "undefined is not an object" yerine anlaşılır bir mesaj çıksın.
    let kod = 'sunucu_hatasi'
    let mesaj = `İstek başarısız (${yanit.status})`
    let detay: string[] | undefined
    try {
      const govdeJson = await yanit.json()
      if (govdeJson?.hata) {
        kod = govdeJson.hata.kod ?? kod
        mesaj = govdeJson.hata.mesaj ?? mesaj
        detay = govdeJson.hata.detay ?? undefined
      }
    } catch {
      /* gövde JSON değil — yukarıdaki varsayılanlar kalır */
    }

    // Yalnız "oturum_gecersiz": giriş ekranının kendi 401'i (kimlik_hatali)
    // oturumu düşürmemeli, kullanıcı zaten oturum açmaya çalışıyor.
    if (yanit.status === 401 && kod === 'oturum_gecersiz') _oturumDustu?.()

    throw new ApiHatasi(yanit.status, kod, mesaj, detay)
  }

  if (yanit.status === 204) return undefined as T
  return (await yanit.json()) as T
}

/**
 * Dosya indirir ve tarayıcıya kaydettirir.
 *
 * `api()` gövdeyi JSON çözüyor; şablon uçları ikili (.xlsx) döndürüyor. Jeton
 * başlığı gerektiği için `<a href>` ile de indirilemiyor — tarayıcı o isteğe
 * Authorization koymaz ve kullanıcı sessizce 401 alırdı.
 */
export async function dosyaIndir(yol: string, varsayilanAd: string): Promise<void> {
  const yanit = await fetch(yol, {
    headers: _jeton ? { Authorization: `Bearer ${_jeton}` } : {},
  })

  if (!yanit.ok) {
    let kod = 'sunucu_hatasi'
    let mesaj = `Dosya alınamadı (${yanit.status})`
    try {
      const govde = await yanit.json()
      if (govde?.hata) {
        kod = govde.hata.kod ?? kod
        mesaj = govde.hata.mesaj ?? mesaj
      }
    } catch {
      /* gövde JSON değil */
    }
    if (yanit.status === 401 && kod === 'oturum_gecersiz') _oturumDustu?.()
    throw new ApiHatasi(yanit.status, kod, mesaj)
  }

  // Sunucunun önerdiği ad varsa o kullanılıyor: dosya adı sözleşmenin parçası
  // ve iki yerde ayrı tutulursa kayar.
  const dispo = yanit.headers.get('Content-Disposition') ?? ''
  const ad = /filename\*?=(?:UTF-8'')?"?([^";]+)"?/i.exec(dispo)?.[1]

  const bag = URL.createObjectURL(await yanit.blob())
  const a = document.createElement('a')
  a.href = bag
  a.download = ad ? decodeURIComponent(ad) : varsayilanAd
  document.body.appendChild(a)
  a.click()
  a.remove()
  // Hemen serbest bırakmak Safari'de indirmeyi yarıda kesiyor.
  setTimeout(() => URL.revokeObjectURL(bag), 10_000)
}

/** Çok parçalı yükleme — `api()` gövdeyi JSON'a çeviriyor, dosya geçmiyor. */
export async function dosyaYukle<T>(yol: string, dosya: File): Promise<T> {
  const form = new FormData()
  form.append('dosya', dosya)

  const yanit = await fetch(yol, {
    method: 'POST',
    headers: _jeton ? { Authorization: `Bearer ${_jeton}` } : {},
    body: form,
  })

  if (!yanit.ok) {
    let kod = 'sunucu_hatasi'
    let mesaj = `Yükleme başarısız (${yanit.status})`
    let detay: string[] | undefined
    try {
      const govde = await yanit.json()
      if (govde?.hata) {
        kod = govde.hata.kod ?? kod
        mesaj = govde.hata.mesaj ?? mesaj
        detay = govde.hata.detay ?? undefined
      }
    } catch {
      /* gövde JSON değil */
    }
    if (yanit.status === 401 && kod === 'oturum_gecersiz') _oturumDustu?.()
    throw new ApiHatasi(yanit.status, kod, mesaj, detay)
  }

  return (await yanit.json()) as T
}
